import React, { useState, useEffect } from "react"
import _ from "lodash"

import { CheckboxInput, DropdownInput, NamedItemInput, NumberInput } from "components/table/carousel/inputs"
import RowAddRemove from "components/table/carousel/row-add-remove"
import { field_attrs } from "components/table/lib/column-field"

import { MultiRowCarouselState_s, CarouselSharedValue_s } from "components/table/carousel/carousel-styles"
import { VacancyLossDetailed_s } from "components/property-market/property-market-styles"

type Item_t = object & {
  i: number
}

const CreditLossDetailed = (props: {rowData: [], field: string, namedItems: {propertyId: number}, persist: Function}) => {
  let initialItems = _.get(props, `rowData.${props.field}.defaultPercentage`) || null;
  let initialOverrideItems = _.get(props, `rowData.${props.field}.overrides`) || null;

  const emptyRecord = {
    percent: null,
    year: 1
  }
  const emptyOverride = {
    percent: null,
    tenantGroupNamedItemId: null,
    excludeSpecRenewals: false
  }

  const [items, setItems] = useState<Item_t[]>(initialItems ? initialItems.map((item, i) => { return {'percent': item, 'year': i+1, i }}) : [{...emptyRecord, i:0}]);
  const [overrides, setOverrides] = useState<Item_t[]>(initialOverrideItems ? initialOverrideItems.map((item, i) => { return {...item, i}}) : [{...emptyOverride, i:0}]);
  const [percentageOverrideToggle, updatePercentageOverrideToggle] = useState<boolean>(initialOverrideItems && initialOverrideItems.length > 0 ? true : false);
  const revenueTypeDefault = _.get(props, `rowData.${props.field}.revenuetype`) || _.get(props, `rowData.${props.field}.revenueType`) || null

  function updateVal(val:any, field:any[]):void {
    let data = _.get(props, `rowData.${props.field}`) && _.get(props, `rowData.${props.field}.state`) === 'detailed' ? _.cloneDeep(_.get(props, `rowData.${props.field}`)) : {state: 'detailed'};
    let nextItems = _.cloneDeep(items);
    nextItems = nextItems.map(item => item.percent);
    _.set(data, 'revenueType', revenueTypeDefault)

    if (!nextItems || !nextItems[0]){
      _.set(data, 'defaultPercentage', null)
      setItems([{...emptyRecord, i:0}]);
    }
    if (!Array.isArray(field)){
      _.set(data, field, val);
    } else {
      if(nextItems[field[0]] === val) return
      nextItems[field[0]] = val
      _.set(data, 'defaultPercentage', nextItems)
      setItems(nextItems.map((item, i) => { return {percent: item, year: i+1, i}}));
    }
    data.state = 'detailed';
    props.persist(data, 'creditlossdetail', props.rowData);
  }

  function updateOverrideVal(val:any, field:any[]):void {
    let data = _.get(props, `rowData.${props.field}`) && _.get(props, `rowData.${props.field}.state`) === 'detailed' ? _.cloneDeep(_.get(props, `rowData.${props.field}`)) : {state: 'detailed'};
    let nextItems = _.cloneDeep(overridesItems);
    nextItems = _.sortBy(nextItems.map(({i, ...rest}) => rest));
    if (!nextItems){
      _.set(data, 'overrides', null)
      setOverrides([{...emptyOverride, i:0}]);
      data.state = 'detailed';
      props.persist(data, 'vacancylossdetail', props.rowData);
      return
    } else {
      if(nextItems[field[0]][field[1]] === val) return
      nextItems[field[0]][field[1]] = val
      _.set(data, 'overrides', nextItems)
      setOverrides(nextItems.map((item, i) => { return {...item, i}}));
    }
    data.state = 'detailed';
    props.persist(data, 'creditlossdetail', props.rowData);
  }

  function updateItems(items:Item_t[]):void {
    let data = _.cloneDeep(_.get(props, `rowData.${props.field}`)) || {};
    let nextItems = items.map((item, i) => { return {...item, year: i+1}});
    setItems(nextItems);
    nextItems = items.map(item => item['percent']);
    _.set(data, 'defaultPercentage', nextItems)
    props.persist(data, 'creditlossdetail', props.rowData);
  }

  function updateOverrides(items:Item_t[]):void {
    let data = _.cloneDeep(_.get(props, `rowData.${props.field}`)) || {};
    setOverrides(items);
    items =  _.sortBy(items.map(({i, ...rest}) => rest));
    if (items.length === 1 && !items[0]['percent'] && !items[0]['tenantGroupNamedItemId']){
      _.set(data, 'overrides', []);
      updatePercentageOverrideToggle(false);
    } else {
      _.set(data, 'overrides', items);
    }
    props.persist(data, 'creditlossdetail', props.rowData);
  }

  function updateOverrideToggle():void {
    if (percentageOverrideToggle){
      updateOverrides([]);
    }
    updatePercentageOverrideToggle(!percentageOverrideToggle)
  }

  useEffect(() => {
    if (!_.get(props, `rowData.${props.field}`) || _.get(props, `rowData.${props.field}.state`) !== 'detailed'){
      setItems([{...emptyRecord, i:0}]);
      setOverrides([{...emptyOverride, i:0}]);
      updatePercentageOverrideToggle(false);
    }
  }, [props['rowData']])

  useEffect(() => {
    if (percentageOverrideToggle === false && _.get(props, `rowData.${props.field}.state`) === 'detailed'){
      props.persist({'defaultPercentage': _.get(props.rowData, "defaultPercentage"), 'state': "detailed", 'reducePercentByVacancy': _.get(props.rowData, "reducePercentByVacancy"), 'revenueType': _.get(props.rowData, "revenuetype")}, 'vacancylossdetail', props.rowData);
      setOverrides([{...emptyOverride, i:0}]);
    }
  }, [percentageOverrideToggle])

  useEffect(() => {
    setItems(initialItems ? initialItems.map((item, i) => { return {'percent': item, 'year': i+1, i }}) : [{...emptyRecord, i:0}]);
    setOverrides(initialOverrideItems ? initialOverrideItems.map((item, i) => { return {...item, i}}) : [{...emptyOverride, i:0}]);
    updatePercentageOverrideToggle(initialOverrideItems && initialOverrideItems.length > 0 ? true : false);
  }, [props.namedItems.propertyId])

  const attrs = (field) => field_attrs('property', 'creditlossdetail')(field)(props.rowData, props)
  const item_attrs = (field) => field_attrs('property', `creditlossdetail.defaultPercentage`)(field[0])(props.rowData, props);
  const override_attrs = (field) => field_attrs('property', `creditlossdetail.overrides`)(field)(props.rowData, props);

  const revenueTypes = [
    'Potential Base Rent',
    'Scheduled Base Rent',
    'Potential Base Rent + Recovery',
    'Potential Base Rent + Scheduled Recovery',
    'Scheduled Base Rent + Recovery',
    'Potential Tenant Revenue',
    'Potential Tenant Revenue w/Scheduled Recovery',
    'Tenant Revenue',
    'Potential Property Revenue',
    'Potential Property Revenue w/Scheduled Recovery',
    'Property Revenue'
  ]

  const params = {
    data: items,
    persist: updateVal
  }
  const overridesParams = {
    data: overrides,
    persist: updateOverrideVal
  }
  let renderItems = _.sortBy(items, 'i');
  let overridesItems = _.sortBy(overrides, 'i');

  if (percentageOverrideToggle && !overridesItems.length){
    overridesItems = [{...emptyOverride, i:0}]
  }

  return (
    <VacancyLossDetailed_s>
      <CarouselSharedValue_s className="carousel-single-row">
        <div className="revenue-type-dropdown">
          <label>Revenue Type</label>
          <DropdownInput className="automated" data={_.get(props, `rowData.${props.field}.state`) === 'detailed' ? _.get(props, `rowData.${props.field}`) : null} attrs={attrs} field="revenueType" default={_.get(props, `rowData.${props.field}.state`) === 'detailed' ? revenueTypeDefault : null} options={revenueTypes} persist={updateVal} />
        </div>
      </CarouselSharedValue_s>
      <div style={{display: 'flex'}}>
        <div style={{marginRight: '20px'}}>
          <div style={{height: '30px'}}><label>Default Percentage</label></div>
          <hr />
          { renderItems.map((row:object, i) => (
            <MultiRowCarouselState_s className="carousel-multi-row">
              <div>
                { i === 0 && <label>Year</label> }
                <NumberInput field={[row['i'], 'year']} {...params} disabled={true} />
              </div>
              <div>
                { i === 0 && <label>Percent</label> }
                <NumberInput className="automated" attrs={item_attrs} field={[row['i'], 'percent']} {...params} readPrecision={2} writePrecision={2} />
              </div>
              <RowAddRemove emptyRecord={emptyRecord} setState={updateItems} row={row['i']} items={renderItems} />
            </MultiRowCarouselState_s>
          ))}
        </div>
        <div className="percentage-overrides" style={{minWidth: '550px'}}>
          <div style={{display: 'flex'}}>
            <label>Percentage Overrides</label>
            <CheckboxInput className="automated" field="percentageOverrideToggle" data={{'percentageOverrideToggle': percentageOverrideToggle}} persist={()=>updateOverrideToggle()} propertyId={props.namedItems.propertyId}/>
          </div>
          <hr />
          { percentageOverrideToggle && overridesItems.map((row:object, i) => (
            <MultiRowCarouselState_s className="carousel-multi-row">
              <div>
                { i === 0 && <label>Percent</label> }
                <NumberInput className="automated" attrs={override_attrs} field={[row['i'], 'percent']} {...overridesParams} readPrecision={2} writePrecision={2} />
              </div>
              <div>
                { i === 0 && <label>Tenant Group</label> }
                <NamedItemInput
                  className="automated"
                  data={overrides}
                  attrs={override_attrs}
                  field={[row['i'], 'tenantGroupNamedItemId']}
                  namedItem="tenantgroupid"
                  options={props.namedItems['tenantGroups']}
                  persist={updateOverrideVal}
                />
              </div>
              <div className="checkbox-div">
                { i === 0 && <label>Base & Options Only?</label> }
                <CheckboxInput className="automated" attrs={override_attrs} field={[row['i'], 'excludeSpecRenewals']} {...overridesParams} propertyId={props.namedItems.propertyId}/>
              </div>
              <RowAddRemove emptyRecord={emptyOverride} setState={updateOverrides} row={row['i']} items={overridesItems} />
            </MultiRowCarouselState_s>
          ))}
        </div>
      </div>
    </VacancyLossDetailed_s>
  )
}

export default CreditLossDetailed

